import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardShell from "@/components/DashboardShell";
import PrintLogoHeader from "@/components/PrintLogoHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api from "@/lib/api";
import { toast } from "sonner";
import { Printer, ArrowLeft } from "lucide-react";

function money(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
}

const _BOOKED = new Set(["scheduled", "closed"]);

function inRange(iso, start) {
  if (!iso) return false;
  try { return new Date(iso) >= start; } catch { return false; }
}

export default function AdvisorScorecardPage() {
  const { advisorId } = useParams();
  const [advisors, setAdvisors] = useState([]);
  const [allDeclines, setAllDeclines] = useState([]);
  const [allAppts, setAllAppts] = useState([]);

  useEffect(() => {
    let live = true;
    Promise.all([
      api.get("/advisors").then((r) => r.data),
      api.get("/declines").then((r) => r.data),
      api.get("/appointments").then((r) => r.data),
    ])
      .then(([advList, decList, apptList]) => {
        if (!live) return;
        setAdvisors(advList);
        setAllDeclines(decList);
        setAllAppts(apptList);
      })
      .catch(() => toast.error("Could not load scorecard"));
    return () => { live = false; };
  }, [advisorId]);

  const rows = useMemo(() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const yearStart = new Date(now.getFullYear(), 0, 1);
    const apptByDecline = new Map();
    allAppts.forEach((a) => { if (a.decline_id) apptByDecline.set(a.decline_id, a); });

    return advisors.map((adv) => {
      const advDeclines = allDeclines.filter((d) => d.advisor_id === adv.id);
      const dec = (rangeStart) => advDeclines.filter((d) => inRange(d.created_at, rangeStart));
      const paid = (rangeStart) =>
        advDeclines.filter(
          (d) =>
            (d.status === "closed" || d.status === "walked_in") &&
            inRange(d.created_at, rangeStart)
        );
      // Revenue matches the dashboard exactly: sum ro_total from every closed
      // appointment for this advisor's declines whose closed_at falls in the
      // range — same aggregation `/api/metrics/overview` uses so the totals
      // row ties out to the dollar.
      const rev = (rangeStart) =>
        allAppts
          .filter((a) => a.status === "closed" && a.decline_id && inRange(a.closed_at, rangeStart))
          .filter((a) => advDeclines.some((d) => d.id === a.decline_id))
          .reduce((s, a) => s + (a.ro_total || 0), 0);

      const decMTD = dec(monthStart);
      const decYTD = dec(yearStart);
      const bookedMTD = decMTD.filter((d) => _BOOKED.has(d.status)).length;
      const bookedYTD = decYTD.filter((d) => _BOOKED.has(d.status)).length;
      const paidMTD = paid(monthStart).length;
      const paidYTD = paid(yearStart).length;

      return {
        id: adv.id,
        name: adv.name,
        color: adv.color || "#0f2b6b",
        declinesMTD: decMTD.length,
        declinesYTD: decYTD.length,
        bookedMTD,
        bookedYTD,
        paidMTD,
        paidYTD,
        closeMTD: decMTD.length > 0 ? (paidMTD / decMTD.length) * 100 : 0,
        closeYTD: decYTD.length > 0 ? (paidYTD / decYTD.length) * 100 : 0,
        revMTD: rev(monthStart),
        revYTD: rev(yearStart),
      };
    });
  }, [advisors, allDeclines, allAppts]);

  const focused = rows.find((r) => r.id === advisorId);
  const totals = useMemo(() => rows.reduce(
    (acc, r) => ({
      declinesMTD: acc.declinesMTD + r.declinesMTD, declinesYTD: acc.declinesYTD + r.declinesYTD,
      bookedMTD: acc.bookedMTD + r.bookedMTD, bookedYTD: acc.bookedYTD + r.bookedYTD,
      paidMTD: acc.paidMTD + r.paidMTD, paidYTD: acc.paidYTD + r.paidYTD,
      revMTD: acc.revMTD + r.revMTD, revYTD: acc.revYTD + r.revYTD,
    }),
    { declinesMTD: 0, declinesYTD: 0, bookedMTD: 0, bookedYTD: 0, paidMTD: 0, paidYTD: 0, revMTD: 0, revYTD: 0 }
  ), [rows]);

  const closeTotalMTD = totals.declinesMTD > 0 ? (totals.paidMTD / totals.declinesMTD) * 100 : 0;
  const closeTotalYTD = totals.declinesYTD > 0 ? (totals.paidYTD / totals.declinesYTD) * 100 : 0;

  return (
    <DashboardShell>
      <PrintLogoHeader docTitle="Advisor Scorecard" />

      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <Link to="/advisors" className="font-mono-label text-muted-foreground hover:text-foreground inline-flex items-center gap-1 no-print" data-testid="scorecard-back">
            <ArrowLeft className="w-3 h-3" strokeWidth={2} /> All Advisors
          </Link>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-2">
            {focused?.name || "Advisor Scorecard"}
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            One row per advisor. Every column ties out with the dashboard totals.
          </p>
        </div>
        <Button onClick={() => window.print()} data-testid="print-scorecard-btn" className="no-print">
          <Printer className="w-4 h-4 mr-2" strokeWidth={1.75} /> Print
        </Button>
      </div>

      <Card className="p-0 overflow-hidden border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead rowSpan={2} className="align-bottom">Advisor</TableHead>
              <TableHead colSpan={5} className="text-center border-l border-border">This Month</TableHead>
              <TableHead colSpan={5} className="text-center border-l border-border">Year-to-Date</TableHead>
            </TableRow>
            <TableRow>
              <TableHead className="text-right border-l border-border">Declines</TableHead>
              <TableHead className="text-right">Booked</TableHead>
              <TableHead className="text-right">Came Back &amp; Paid</TableHead>
              <TableHead className="text-right">Close %</TableHead>
              <TableHead className="text-right">Dollars Recovered</TableHead>
              <TableHead className="text-right border-l border-border">Declines</TableHead>
              <TableHead className="text-right">Booked</TableHead>
              <TableHead className="text-right">Came Back &amp; Paid</TableHead>
              <TableHead className="text-right">Close %</TableHead>
              <TableHead className="text-right">Dollars Recovered</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow className="bg-navy/5 dark:bg-navy/20 border-b-2 border-navy/30 font-semibold" data-testid="scorecard-totals-row">
              <TableCell className="font-display font-black text-navy dark:text-blue-200">TEAM TOTALS</TableCell>
              <TableCell className="text-right tabular-nums border-l border-border">{totals.declinesMTD}</TableCell>
              <TableCell className="text-right tabular-nums">{totals.bookedMTD}</TableCell>
              <TableCell className="text-right tabular-nums">{totals.paidMTD}</TableCell>
              <TableCell className="text-right tabular-nums">{closeTotalMTD.toFixed(1)}%</TableCell>
              <TableCell className="text-right tabular-nums text-navy dark:text-blue-200 font-bold">{money(totals.revMTD)}</TableCell>
              <TableCell className="text-right tabular-nums border-l border-border">{totals.declinesYTD}</TableCell>
              <TableCell className="text-right tabular-nums">{totals.bookedYTD}</TableCell>
              <TableCell className="text-right tabular-nums">{totals.paidYTD}</TableCell>
              <TableCell className="text-right tabular-nums">{closeTotalYTD.toFixed(1)}%</TableCell>
              <TableCell className="text-right tabular-nums text-navy dark:text-blue-200 font-bold">{money(totals.revYTD)}</TableCell>
            </TableRow>
            {rows.map((r) => (
              <TableRow key={r.id} className="hover:bg-muted/40" data-testid={`scorecard-row-${r.id}`}>
                <TableCell>
                  <div className="flex items-center gap-2.5">
                    <div className="w-2.5 h-2.5 rounded-full ring-2 ring-background shadow-sm" style={{ backgroundColor: r.color }} />
                    <Link to={`/advisor/${r.id}`} className="hover:underline" style={{ color: r.color }}>{r.name}</Link>
                  </div>
                </TableCell>
                <TableCell className="text-right tabular-nums border-l border-border">{r.declinesMTD}</TableCell>
                <TableCell className="text-right tabular-nums">{r.bookedMTD}</TableCell>
                <TableCell className="text-right tabular-nums">{r.paidMTD}</TableCell>
                <TableCell className="text-right tabular-nums">{r.closeMTD.toFixed(1)}%</TableCell>
                <TableCell className="text-right tabular-nums text-navy dark:text-blue-200">{money(r.revMTD)}</TableCell>
                <TableCell className="text-right tabular-nums border-l border-border">{r.declinesYTD}</TableCell>
                <TableCell className="text-right tabular-nums">{r.bookedYTD}</TableCell>
                <TableCell className="text-right tabular-nums">{r.paidYTD}</TableCell>
                <TableCell className="text-right tabular-nums">{r.closeYTD.toFixed(1)}%</TableCell>
                <TableCell className="text-right tabular-nums text-navy dark:text-blue-200">{money(r.revYTD)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </DashboardShell>
  );
}
